// ================================================================
// 管理員系統設定頁面
// ================================================================
// 版本：2.0
// 日期：2026-01-26
// 功能：體驗點數、透支額度等系統設定
// ================================================================

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Save,
  Refresh,
  CardGiftcard,
  AccountBalanceWallet,
  Warning,
  Info,
  QuestionAnswer,
  PriceChange,
  Campaign,
  Backup,
} from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  getSystemSettings,
  updateSystemSettings,
  SystemSettings,
} from '../../api/admin';

const AdminSettingsPage: React.FC = () => {
  // 狀態
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // 表單狀態
  const [registrationBonusPoints, setRegistrationBonusPoints] = useState('499');
  const [registrationBonusDays, setRegistrationBonusDays] = useState('30');
  const [defaultDebtLimit, setDefaultDebtLimit] = useState('500');
  const [debtWarningThreshold, setDebtWarningThreshold] = useState('300');

  // 載入設定
  const loadSettings = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getSystemSettings();
      if (response.success && response.settings) {
        setSettings(response.settings);
        // 更新表單值
        if (response.settings.registration_bonus_points) {
          setRegistrationBonusPoints(response.settings.registration_bonus_points.value);
        }
        if (response.settings.registration_bonus_days) {
          setRegistrationBonusDays(response.settings.registration_bonus_days.value);
        }
        if (response.settings.default_debt_limit) {
          setDefaultDebtLimit(response.settings.default_debt_limit.value);
        }
        if (response.settings.debt_warning_threshold) {
          setDebtWarningThreshold(response.settings.debt_warning_threshold.value);
        }
      } else {
        setError(response.message || '無法載入設定');
      }
    } catch (err: any) {
      setError(err.message || '載入失敗');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // 儲存設定
  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    // 驗證
    const bonusPoints = parseInt(registrationBonusPoints);
    const bonusDays = parseInt(registrationBonusDays);
    const debtLimit = parseInt(defaultDebtLimit);
    const warningThreshold = parseInt(debtWarningThreshold);

    if (isNaN(bonusPoints) || bonusPoints < 0) {
      setError('註冊贈送點數必須是非負整數');
      setIsSaving(false);
      return;
    }
    if (isNaN(bonusDays) || bonusDays < 1) {
      setError('點數有效天數必須是正整數');
      setIsSaving(false);
      return;
    }
    if (isNaN(debtLimit) || debtLimit < 0) {
      setError('透支額度上限必須是非負整數');
      setIsSaving(false);
      return;
    }
    if (isNaN(warningThreshold) || warningThreshold < 0) {
      setError('透支警告閾值必須是非負整數');
      setIsSaving(false);
      return;
    }
    if (warningThreshold > debtLimit) {
      setError('透支警告閾值不能大於透支額度上限');
      setIsSaving(false);
      return;
    }

    try {
      const response = await updateSystemSettings({
        registration_bonus_points: bonusPoints.toString(),
        registration_bonus_days: bonusDays.toString(),
        default_debt_limit: debtLimit.toString(),
        debt_warning_threshold: warningThreshold.toString(),
      });

      if (response.success) {
        setSuccess(response.message || '設定已儲存');
        loadSettings(); // 重新載入
      } else {
        setError(response.message || '儲存失敗');
      }
    } catch (err: any) {
      setError(err.message || '儲存失敗');
    } finally {
      setIsSaving(false);
    }
  };

  // 檢查是否有變更
  const hasChanges = () => {
    if (!settings) return false;
    return (
      registrationBonusPoints !== (settings.registration_bonus_points?.value || '499') ||
      registrationBonusDays !== (settings.registration_bonus_days?.value || '30') ||
      defaultDebtLimit !== (settings.default_debt_limit?.value || '500') ||
      debtWarningThreshold !== (settings.debt_warning_threshold?.value || '300')
    );
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        {/* 頁面標題 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              系統設定
            </Typography>
            <Typography variant="body2" color="text.secondary">
              管理註冊點數、透支額度等系統參數
            </Typography>
          </Box>
          <Box>
            <Tooltip title="重新載入">
              <IconButton onClick={loadSettings} sx={{ mr: 1 }}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <Save />}
              onClick={handleSave}
              disabled={isSaving || !hasChanges()}
            >
              儲存設定
            </Button>
          </Box>
        </Box>

        {/* 訊息提示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* 體驗點數設定 */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <CardGiftcard color="secondary" />
                  <Typography variant="h6" fontWeight="bold">
                    體驗點數設定
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <TextField
                  fullWidth
                  label="註冊贈送點數"
                  type="number"
                  value={registrationBonusPoints}
                  onChange={(e) => setRegistrationBonusPoints(e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">點</InputAdornment>,
                  }}
                  helperText="新使用者註冊時贈送的體驗點數"
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="點數有效期限"
                  type="number"
                  value={registrationBonusDays}
                  onChange={(e) => setRegistrationBonusDays(e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">天</InputAdornment>,
                  }}
                  helperText="體驗點數的有效天數，過期後自動清零"
                />

                <Alert severity="info" icon={<Info />} sx={{ mt: 2 }}>
                  目前設定：新使用者註冊後獲得 {registrationBonusPoints} 點，有效期 {registrationBonusDays} 天
                </Alert>
              </CardContent>
            </Card>
          </Grid>

          {/* 透支額度設定 */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <AccountBalanceWallet color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    透支額度設定
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <TextField
                  fullWidth
                  label="預設透支額度上限"
                  type="number"
                  value={defaultDebtLimit}
                  onChange={(e) => setDefaultDebtLimit(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">NT$</InputAdornment>,
                  }}
                  helperText="新使用者的預設透支額度上限"
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="透支警告閾值"
                  type="number"
                  value={debtWarningThreshold}
                  onChange={(e) => setDebtWarningThreshold(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">NT$</InputAdornment>,
                  }}
                  helperText="當透支超過此金額時顯示警告"
                />

                <Alert
                  severity={parseInt(debtWarningThreshold) > parseInt(defaultDebtLimit) ? 'error' : 'warning'}
                  icon={<Warning />}
                  sx={{ mt: 2 }}
                >
                  透支超過 NT${debtWarningThreshold} 時將顯示警告，超過 NT${defaultDebtLimit} 時將暫停服務
                </Alert>
              </CardContent>
            </Card>
          </Grid>

          {/* 其他管理功能快速連結 */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                其他管理功能
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<QuestionAnswer />}
                    onClick={() => window.location.href = '/admin/questions'}
                    sx={{ py: 2 }}
                  >
                    問題管理
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PriceChange />}
                    onClick={() => window.location.href = '/admin/price'}
                    sx={{ py: 2 }}
                  >
                    價格管理
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Campaign />}
                    onClick={() => window.location.href = '/admin/announcements'}
                    sx={{ py: 2 }}
                  >
                    公告管理
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Backup />}
                    onClick={() => window.location.href = '/admin/backup'}
                    sx={{ py: 2 }}
                  >
                    備份管理
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </AdminLayout>
  );
};

export default AdminSettingsPage;
